/** @format */

import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { RootState } from "./store";
import { setUserToken, UserToken } from "../features/auth/authSlice";
import { fetchTokenWithRefreshToken as fetchHwbTokenWithRefreshToken } from "../features/auth/hwbAuth/hwbService";
import { fetchTokenWithRefreshToken as fetchBBTokenWithRefreshToken } from "../features/auth/bbOauth/bbOauthService";
import { removeCookie } from "../utils/cookies";

const baseQuery = fetchBaseQuery({
  baseUrl:
    process.env.REACT_APP_RESOURCES_API_URL ??
    "https://36h5qfaq9c.execute-api.eu-west-2.amazonaws.com/dev/",
  prepareHeaders: (headers, { getState, extra, endpoint, type, forced }) => {
    const rootState = getState() as RootState;
    const token = rootState.auth?.userToken?.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
  },
});

/** Function to attempt baseQuery and will retry if token needs refresh */
const baseQueryWithReAuthentication: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let response = await baseQuery(args, api, extraOptions);

  const clearFailedToken = (error: unknown) => {
    console.error(error);
    api.dispatch(setUserToken(undefined));
    localStorage.removeItem("BL_CMS_STATE");
    process.env.REACT_APP_FLAG_DEV ?? removeCookie('user_token')
    api.dispatch(apiSlice.util.invalidateTags(["User"]));
  };

  if (response.error) {
    const rootState = api.getState() as RootState;
    const userToken = rootState.auth?.userToken;

    // If expired, refresh token to attempt to resolve query error
    if (userToken && new Date(userToken.expires) < new Date()) {
      const saveNewToken = (newToken: UserToken | void) => {
        if (newToken) {
          console.log("new token");
          api.dispatch(setUserToken(newToken));
        } else {
          // Clear expired token if no new token is recieved
          console.error("No new token provided, user token cleared");
          api.dispatch(setUserToken(undefined));
          localStorage.removeItem("BL_CMS_STATE");
          process.env.REACT_APP_FLAG_DEV ?? removeCookie('user_token')
          api.dispatch(apiSlice.util.invalidateTags(["User"]));
        }
      };

      switch (userToken.service) {
        case "hwb":
          await fetchHwbTokenWithRefreshToken(userToken.refreshToken).then(
            saveNewToken,
            clearFailedToken,
          );
          break;
        case "bitbucket":
          await fetchBBTokenWithRefreshToken(userToken.refreshToken).then(
            saveNewToken,
            clearFailedToken,
          );
          break;
      }

      // Reattempt original query
      response = await baseQuery(args, api, extraOptions);
    }
  }

  return response;
};

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReAuthentication,
  tagTypes: [
    "Resources",
    "ResourceRef",
    "UserResourceRef",
    "UserResources",
    "User",
  ],
  endpoints: (builder) => ({}),
});
